import dayjs from "dayjs";
import { PLATFORM } from "core-lib/data/common";
import { getImageKitUrl } from "core-lib/utils/imageKit";
import {
  Blog,
  ComponentType,
  DiscoverContentType,
  Tag,
  Image,
  Insight,
  Interest,
  LibraryType,
  News,
  Playlist,
  Section,
  Short,
  Video,
  View,
  quizType,
} from "../types";
import { isURL } from "core-lib/utils/regex";
import { isValidMobileVersion } from "core-lib/utils/nativeBridge";

export function formatMenuData(data: any) {
  return {
    showWelcome: data.show_welcome_screen,
    welcomeScreen: {
      sneakPeakCompId: data?.welcome_screen?.sneak_peek_comp_id,
    },
    tabs: data?.sections?.map((s) => ({
      id: s.id,
      order: s.order,
      title: s?.content?.title,
      sections: [],
    })),
  };
}

export function formatInterestsData(data: any): Interest[] {
  return data?.map((item) => ({
    icon: item.icon,
    title: item.title,
    isSelected: item.is_selected,
    id: item.id,
    bgImg: item.bg_img,
  }));
}

export function formatSectionsData(sections): Section[] {
  return sections.map((section) => ({
    id: section?.id,
    order: section?.order,
    uiType: section?.ui_meta.layout,
    title: section?.content?.title,
    contentType: section?.ui_meta.ui_type,
    libraryType: section?.ui_meta?.library_type,
    upFrontCardsCount: section?.upfront_card_count,
    items: [],
  }));
}
export function formatViewsData(data: any): View[] {
  return data?.map((item) => ({
    id: item.id,
    title: item.title,
    order: item.order,
    sections: formatSectionsData(item.sections),
  }));
}

export function formatInsightItem(item): Insight {
  return {
    id: item.id,
    title:
      item?.ui_meta?.secondary_bg_img && item?.content?.title
        ? item?.content?.title
        : null,
    thumb:
      item?.ui_meta?.secondary_bg_img && item?.content?.title
        ? getImageKitUrl({
            imageUrl: item?.ui_meta?.secondary_bg_img,
            product: "Strapi",
            cdnType: "ImageKit",
            quality: 90,
          })
        : getImageKitUrl({
            imageUrl: item?.ui_meta?.background_img,
            product: "Strapi",
            cdnType: "ImageKit",
            quality: 90,
          }),
    url: item?.ui_meta?.url,
    isBookmark: item?.is_bookmarked,
    isLiked: item?.is_liked,
    order: item?.order,
    likeCount: item?.like_count,
    parentId: item?.parent_id,
    uiType: item?.ui_meta?.ui_type,
    searchCategory: item?.search_category,
    shareText: item?.share_text,
  };
}

export function formatTagItem(item): Tag {
  return {
    id: item.id,
    title: item?.title,
    thumb: getImageKitUrl({
      imageUrl: item?.bg_img,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 90,
      height: 185,
    }),
    icon: item?.icon,
    isSelected: item?.is_selected,
    parentId: item?.parent_id,
  };
}

export function formatVideoItem(item): Video {
  return {
    id: item?.id,
    title: item?.content?.title,
    isLiked: item.is_liked,
    isChannelFollowed: item.is_channel_followed,
    url: item.ui_meta.video_url,
    isYoutube: !isURL(item.ui_meta.video_url),
    viewCount: (item?.video_youtube_views ?? 0) + (item?.view_count ?? 0),
    thumb: getImageKitUrl({
      imageUrl: item?.ui_meta.background_img,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 90,
      height: 240,
    }),
    date: item?.created_at,
    shareText: item?.share_text,
    likeCount: item?.like_count,
    duration: item?.video_duration_sec,
    isBookmark: item?.is_bookmarked,
    author: { name: item?.content?.short_title, avatar: item?.ui_meta?.icon },
    parentId: item?.parent_id,
    order: item?.order,
    uiType: item?.ui_meta?.ui_type,
    searchCategory: item?.search_category,
  };
}
export function formatPlaylistItem(item): Playlist {
  return {
    id: item?.id,
    title: item?.content?.title,
    isLiked: item.is_liked,
    isChannelFollowed: item.is_channel_followed,
    url: item.ui_meta.video_url,
    isYoutube: !isURL(item.ui_meta.video_url),
    viewCount: (item?.video_youtube_views ?? 0) + (item?.view_count ?? 0),
    thumb: getImageKitUrl({
      imageUrl: item?.ui_meta.background_img,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 90,
      height: 240,
    }),
    date: item?.created_at ?? item?.updated_at,
    shareText: item?.share_text,
    likeCount: item?.like_count,
    duration: item?.video_duration_sec,
    isBookmark: item?.is_bookmarked,
    author: { name: item?.content?.short_title, avatar: item?.ui_meta?.icon },
    parentId: item?.parent_id,
    videos: item?.card_ids,
    order: item?.order,
    uiType: item?.ui_meta?.ui_type,
    searchCategory: item?.search_category,
  };
}

export function formatNewsItem(item): News {
  return {
    id: item.id,
    title: item?.content?.title,
    date: dayjs(item?.published_at).format("DD MMM, YY"),
    thumb: getImageKitUrl({
      imageUrl: item?.ui_meta?.background_img,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 90,
      height: 100,
      width: 100,
    }),
    author: { name: item?.publisher?.name, avatar: item?.publisher?.icon },
    url: item?.ui_meta?.url,
    shareText: item?.share_text,
    isBookmark: item?.is_bookmarked,
    isLiked: item?.is_liked,
    parentId: item?.parent_id,
    order: item?.order,
    uiType: item?.ui_meta?.ui_type,
    searchCategory: item?.search_category,
  };
}

export function formatShortsItem(item): Short {
  return {
    id: item?.id,
    title: item?.content?.title,
    date: dayjs(item?.published_at).format("DD MMM, YY"),
    thumb: getImageKitUrl({
      imageUrl: item?.ui_meta?.background_img,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 90,
    }),
    url: item?.ui_meta?.url,
    isBookmark: item?.is_bookmarked,
    isLiked: item?.is_liked,
    author: {
      name: item?.content?.short_title,
      avatar: item?.ui_meta?.icon,
    },
    shareText: item?.share_text,
    parentId: item?.parent_id,
    videoId: item.ui_meta.video_url,
    isYoutube: !isURL(item.ui_meta.video_url),
    videoThumbnail: item?.ui_meta?.background_img,
    channelName: item?.content?.short_title,
    channelLogo:
      item?.ui_meta?.icon && item?.ui_meta?.icon !== ""
        ? item?.ui_meta?.icon
        : "/app/img/icons/guest-user.svg",
    likeCount: item?.like_count,
    viewCount: item?.video_youtube_views ?? item?.view_count,
    shareMsg: item?.share_text,
    isChannelFollowed: item?.is_channel_followed,
    uiType: item?.ui_meta?.ui_type,
    order: item?.order,
    searchCategory: item?.search_category,
  };
}

export function formatImageItem(item): Image {
  return {
    id: item?.id,
    title: item?.content?.description,
    date: item?.published_at,
    thumb: getImageKitUrl({
      imageUrl: item?.ui_meta?.url,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 90,
    }),
    url: item?.ui_meta?.url,
    isBookmark: item?.is_bookmarked,
    isLiked: item?.is_liked,
    author: { name: item?.publisher?.name, avatar: item?.publisher?.icon },
    shareText: item?.share_text,
    likeCount: item?.like_count,
    parentId: item?.parent_id,
    uiType: item?.ui_meta?.ui_type,
    order: item?.order,
    searchCategory: item?.search_category,
  };
}

export function formatBlogItem(item): Blog {
  return {
    id: item?.id,
    title: item?.content?.title,
    description: item?.content?.description,
    date: dayjs(item?.published_at).format("DD MMM, YY"),
    thumb: getImageKitUrl({
      imageUrl: item?.ui_meta?.background_img,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 90,
      height: 246,
    }),
    url: item?.ui_meta?.url,
    isBookmark: item?.is_bookmarked,
    author: {
      name: item?.publisher?.name,
      avatar: item?.publisher?.icon,
    },
    shareText: item?.share_text,
    viewCount: item?.view_count,
    order: item?.order,
    parentId: item?.parent_id,
    uiType: item?.ui_meta?.ui_type,
    searchCategory: item?.search_category,
  };
}

export function formatQuizItem(item, questions): quizType {
  const question = questions[item.question_ids[0]];

  return {
    correctOptionId: question?.correct_option_id,
    options: question?.options?.map((option) => ({
      optionId: option?.id,
      optionName: option?.option_text,
      votes: option?.votes,
    })),
    id: question?.question_id,
    text: question?.question_text,
    title: question?.question_text,
    selectedOptionId: question?.selected_option_id,
    questionType: question?.question_type,
    description: item?.content?.description,
    uiType: item?.ui_meta?.ui_type,
    backgroundImage: getImageKitUrl({
      imageUrl: item?.ui_meta?.background_img,
      product: "Strapi",
      cdnType: "ImageKit",
      quality: 100,
    }),
  };
}

export function formatBlogItems(data): Blog[] {
  return data.map((item) => formatBlogItem(item));
}
export function formatTagItems(data): Tag[] {
  return data.map((item) => formatTagItem(item));
}
export function formatImageItems(data): Image[] {
  return data.map((item) => formatImageItem(item));
}
export function formatInsightItems(data): Insight[] {
  return data.map((item) => formatInsightItem(item));
}
export function formatNewsItems(data): News[] {
  return data.map((item) => formatNewsItem(item));
}
export function formatPlaylistItems(data): Playlist[] {
  return data.map((item) => formatPlaylistItem(item));
}
export function formatShortItems(data): Short[] {
  return data.map((item) => formatShortsItem(item));
}
export function formatVideoItems(data): Video[] {
  return data.map((item) => formatVideoItem(item));
}
export function formatQuizItems(data, questions): quizType[] {
  return data.map((item) => formatQuizItem(item, questions));
}

export function formatItemsData(
  data,
  contentType: DiscoverContentType,
  libraryType: LibraryType | null,
  questions = {}
): ComponentType {
  switch (contentType) {
    case DiscoverContentType.BLOG:
      return formatBlogItems(data);
    case DiscoverContentType.IMAGE:
      return formatImageItems(data);
    case DiscoverContentType.STORY:
      return formatInsightItems(data);
    case DiscoverContentType.NEWS:
      return formatNewsItems(data);
    case DiscoverContentType.SHORT:
      return formatShortItems(data);
    case DiscoverContentType.PLAYLIST:
      return formatPlaylistItems(data);
    case DiscoverContentType.VIDEO:
      return formatVideoItems(data);
    case DiscoverContentType.TAG:
      return formatTagItems(data);
    case DiscoverContentType.QUIZ:
      return formatQuizItems(data, questions);
    default:
      return [];
  }
}

export function formatSavedSearchItems(data) {
  const results = {
    blogs: [] as Blog[],
    images: [] as Image[],
    news: [] as News[],
    videos: [] as Video[],
    shorts: [] as Short[],
    stories: [] as Insight[],
  };
  const chips: string[] = [];

  for (let idx = 0; idx < data.length; idx++) {
    const curr = data[idx];
    const uiType = curr?.ui_meta?.ui_type;

    switch (uiType) {
      case DiscoverContentType.BLOG:
        results.blogs.push(formatBlogItem(curr));
        break;
      case DiscoverContentType.IMAGE:
        results.images.push(formatImageItem(curr));
        break;
      case DiscoverContentType.NEWS:
        results.news.push(formatNewsItem(curr));
        break;
      case DiscoverContentType.PLAYLIST:
        results.videos.push(formatPlaylistItem(curr));
        break;
      case DiscoverContentType.SHORT:
        if (
          isValidMobileVersion({platform:PLATFORM.android})
        ) {
          results.shorts.push(formatShortsItem(curr));
        }
        break;
      case DiscoverContentType.STORY:
        results.stories.push(formatInsightItem(curr));
        break;
      case DiscoverContentType.VIDEO:
        results.videos.push(formatVideoItem(curr));
        break;
    }
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const key in results) {
    if (results[key].length) {
      chips.push(key.charAt(0).toUpperCase() + key.slice(1));
    }
  }

  return { results, chips };
}

export function formatRecentSearch(data) {
  return data?.map((e) => e?.search_query);
}

export function formatSuggestion(data) {
  return data?.map((e) => e?.title);
}

export function formatCardData(data, uiType) {
  switch (uiType) {
    case DiscoverContentType.BLOG:
      return formatBlogItem(data);
    case DiscoverContentType.IMAGE:
      return formatImageItem(data);
    case DiscoverContentType.NEWS:
      return formatNewsItem(data);
    case DiscoverContentType.PLAYLIST:
      return formatPlaylistItem(data);
    case DiscoverContentType.SHORT:
      return formatShortsItem(data);
    case DiscoverContentType.STORY:
      return formatInsightItem(data);
    case DiscoverContentType.VIDEO:
      return formatVideoItem(data);
    // case DiscoverContentType.QUIZ:
    //   return formatQuizItem(data);
  }
}

export function formatFollowData(item) {
  return item.map((e) => ({
    name: e?.channel_name,
  }));
}
